import React, { useState, useEffect } from 'react';
import { TruckIcon, WrenchScrewdriverIcon, CurrencyDollarIcon, UserCircleIcon, DocumentCheckIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import ImageUploadDropzone from './ImageUploadDropzone';
import { Vehicle, VehicleFormData, VehicleStatus, FuelType } from '../../types/vehicle';
import { useVehicles } from '../../hooks/useVehicles';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useUsers } from '../../hooks/useUsers';
import { useLanguage } from '../../contexts/LanguageContext';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';
import { generateVehicleSlug } from '../../utils/slugify';

// Helper to convert Timestamp to Date
const toDate = (date: any): Date | undefined => {
  if (!date) return undefined;
  if (date.toDate && typeof date.toDate === 'function') {
    return date.toDate();
  }
  if (date instanceof Date) return date;
  return new Date(date);
};

interface VehicleEditorProps {
  vehicle: Vehicle | null;
  onClose: () => void;
}

const VehicleEditor: React.FC<VehicleEditorProps> = ({ vehicle, onClose }) => {
  const { t } = useLanguage();
  const { create, update } = useVehicles();
  const { organizations } = useOrganizations();
  const { users } = useUsers();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<VehicleFormData>({
    licensePlate: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    vin: '',
    color: '',
    fuelType: 'gasoline',
    customerId: '',
    ownerName: '',
    assignedTo: '',
    registrationNumber: '',
    vehicleLogbookNumber: '',
    mileage: 0, // Alapértelmezett 0, de nem kötelező kitölteni
    engineCapacity: undefined,
    power: undefined,
    seats: undefined,
    hasTowbar: false,
    status: 'active',
    purchaseDate: undefined,
    purchasePrice: undefined,
    notes: '',
    // Rental settings
    rentalAvailable: false,
    category: undefined,
    pricePerDay: undefined,
    currency: 'HUF',
    transmission: undefined,
    features: [],
    imageUrl: undefined,
    photos: [],
    minRentalDays: 1,
    maxRentalDays: undefined,
    depositAmount: undefined,
    descriptionHu: undefined,
    descriptionEn: undefined,
    quantity: undefined
  });

  useEffect(() => {
    if (vehicle) {
      setFormData({
        licensePlate: vehicle.licensePlate,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
        vin: vehicle.vin,
        color: vehicle.color,
        fuelType: vehicle.fuelType,
        customerId: vehicle.customerId,
        ownerName: vehicle.ownerName,
        assignedTo: vehicle.assignedTo,
        registrationNumber: vehicle.registrationNumber,
        vehicleLogbookNumber: vehicle.vehicleLogbookNumber,
        mileage: vehicle.mileage,
        engineCapacity: vehicle.engineCapacity,
        power: vehicle.power,
        seats: vehicle.seats,
        hasTowbar: vehicle.hasTowbar,
        status: vehicle.status,
        purchaseDate: toDate(vehicle.purchaseDate),
        purchasePrice: vehicle.purchasePrice,
        notes: vehicle.notes || '',
        // Rental settings
        rentalAvailable: vehicle.rentalAvailable || false,
        category: vehicle.category,
        pricePerDay: vehicle.pricePerDay,
        currency: vehicle.currency || 'HUF',
        transmission: vehicle.transmission,
        features: vehicle.features || [],
        imageUrl: vehicle.imageUrl,
        photos: vehicle.photos || [],
        minRentalDays: vehicle.minRentalDays || 1,
        maxRentalDays: vehicle.maxRentalDays,
        depositAmount: vehicle.depositAmount,
        descriptionHu: vehicle.descriptionHu,
        descriptionEn: vehicle.descriptionEn,
        quantity: vehicle.quantity
      });
    }
  }, [vehicle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Automatikus slug generálás, ha nincs vagy ha változott a márka/modell/év
      const shouldGenerateSlug = !formData.slug || 
        (vehicle && (
          vehicle.make !== formData.make || 
          vehicle.model !== formData.model || 
          vehicle.year !== formData.year
        ));

      const dataToSave = {
        ...formData,
        slug: shouldGenerateSlug 
          ? generateVehicleSlug(formData.make, formData.model, formData.year, vehicle?.id?.slice(-4))
          : formData.slug
      };

      if (vehicle) {
        await update(vehicle.id, dataToSave);
        toast.success(t('fleet.messages.updated'));
      } else {
        const newVehicleId = await create(dataToSave);
        // Új jármű esetén frissítjük a slug-ot az ID-val
        if (newVehicleId && typeof newVehicleId === 'string') {
          const finalSlug = generateVehicleSlug(formData.make, formData.model, formData.year, newVehicleId.slice(-4));
          await update(newVehicleId, { slug: finalSlug });
        }
        toast.success(t('fleet.messages.created'));
      }
      onClose();
    } catch (error: any) {
      toast.error(`${t('fleet.messages.error')}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof VehicleFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={vehicle ? t('fleet.form.titleEdit') : t('fleet.form.titleNew')}
      size="6xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6 p-6">
        {/* Alapadatok Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <TruckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('fleet.form.sections.basicInfo')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Rendszám */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.licensePlate')} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.licensePlate}
                  onChange={(e) => handleChange('licensePlate', e.target.value.toUpperCase())}
                  placeholder={t('fleet.form.fields.licensePlatePlaceholder')}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Státusz */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.status')}
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value as VehicleStatus)}
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
                >
                  <option value="active">{t('fleet.status.active')}</option>
                  <option value="inactive">{t('fleet.status.inactive')}</option>
                  <option value="maintenance">{t('fleet.status.maintenance')}</option>
                  <option value="sold">{t('fleet.status.sold')}</option>
                </select>
              </div>

              {/* Márka */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.make')} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.make}
                  onChange={(e) => handleChange('make', e.target.value)}
                  placeholder={t('fleet.form.fields.makePlaceholder')}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Modell */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.model')} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.model}
                  onChange={(e) => handleChange('model', e.target.value)}
                  placeholder={t('fleet.form.fields.modelPlaceholder')}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Gyártási év */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.year')} *
                </label>
                <input
                  type="number"
                  required
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  value={formData.year}
                  onChange={(e) => handleChange('year', parseInt(e.target.value))}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Üzemanyag típus */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.fuelType')} *
                </label>
                <select
                  required
                  value={formData.fuelType}
                  onChange={(e) => handleChange('fuelType', e.target.value as FuelType)}
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
                >
                  <option value="gasoline">{t('fleet.form.fuelTypes.gasoline')}</option>
                  <option value="diesel">{t('fleet.form.fuelTypes.diesel')}</option>
                  <option value="electric">{t('fleet.form.fuelTypes.electric')}</option>
                  <option value="hybrid">{t('fleet.form.fuelTypes.hybrid')}</option>
                  <option value="lpg">{t('fleet.form.fuelTypes.lpg')}</option>
                  <option value="cng">{t('fleet.form.fuelTypes.cng')}</option>
                </select>
              </div>

              {/* Km óra - opcionális, később rögzíthető */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.mileage')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.mileage || ''}
                  onChange={(e) => handleChange('mileage', e.target.value ? parseInt(e.target.value) : 0)}
                  placeholder={t('fleet.form.fields.mileagePlaceholder')}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('fleet.form.fields.mileageHelp')}
                </p>
              </div>

              {/* Szín */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.color')}
                </label>
                <input
                  type="text"
                  value={formData.color || ''}
                  onChange={(e) => handleChange('color', e.target.value)}
                  placeholder={t('fleet.form.fields.colorPlaceholder')}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Alvázszám */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.vin')}
                </label>
                <input
                  type="text"
                  value={formData.vin || ''}
                  onChange={(e) => handleChange('vin', e.target.value.toUpperCase())}
                  placeholder={t('fleet.form.fields.vinPlaceholder')}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Forgalmi engedély száma */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.registrationNumber')}
                </label>
                <input
                  type="text"
                  value={formData.registrationNumber || ''}
                  onChange={(e) => handleChange('registrationNumber', e.target.value)}
                  placeholder={t('fleet.form.fields.registrationNumberPlaceholder')}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Törzskönyv száma */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.vehicleLogbookNumber')}
                </label>
                <input
                  type="text"
                  value={formData.vehicleLogbookNumber || ''}
                  onChange={(e) => handleChange('vehicleLogbookNumber', e.target.value)}
                  placeholder={t('fleet.form.fields.vehicleLogbookNumberPlaceholder')}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Tulajdonos szervezet */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.ownerOrganization')}
                </label>
                <select
                  value={formData.customerId || ''}
                  onChange={(e) => {
                    handleChange('customerId', e.target.value || undefined);
                    // Ha van kiválasztott szervezet, töröljük a kézi tulajdonos nevet
                    if (e.target.value) {
                      handleChange('ownerName', undefined);
                    }
                  }}
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
                >
                  <option value="">{t('fleet.form.fields.ownerOrganizationNone')}</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('fleet.form.fields.ownerOrganizationHelp')}
                </p>
              </div>

              {/* Tulajdonos neve (ha nem CRM ügyfél) */}
              {!formData.customerId && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('fleet.form.fields.ownerName')}
                  </label>
                  <input
                    type="text"
                    value={formData.ownerName || ''}
                    onChange={(e) => handleChange('ownerName', e.target.value)}
                    placeholder={t('fleet.form.fields.ownerNamePlaceholder')}
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('fleet.form.fields.ownerNameHelp')}
                  </p>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Műszaki adatok Section - TILTOTT */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 relative">
          <div className="absolute inset-0 bg-gray-900/5 dark:bg-gray-900/20 backdrop-blur-[0.5px] z-10 rounded-lg flex items-center justify-center">
            <div className="bg-white/95 dark:bg-gray-800/95 px-4 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">🔒 Ez a funkció jelenleg nem elérhető</p>
            </div>
          </div>
          <div className="flex pointer-events-none opacity-70">
            <div className="p-4 bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <WrenchScrewdriverIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('fleet.form.sections.technicalData')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Hengerűrtartalom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.engineCapacity')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.engineCapacity || ''}
                  onChange={(e) => handleChange('engineCapacity', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Teljesítmény */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.power')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.power || ''}
                  onChange={(e) => handleChange('power', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Ülések száma */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.seats')}
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.seats || ''}
                  onChange={(e) => handleChange('seats', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Vonóhorog */}
              <div className="flex items-center">
                <label className="relative flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.hasTowbar || false}
                    onChange={(e) => handleChange('hasTowbar', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">{t('fleet.form.fields.hasTowbar')}</span>
                </label>
              </div>

              {/* Vásárlási ár */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.purchasePrice')}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.purchasePrice || ''}
                  onChange={(e) => handleChange('purchasePrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Megjegyzések */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fleet.form.fields.notes')}
                </label>
                <textarea
                  rows={3}
                  value={formData.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vezető/Sofőr Section - TILTOTT */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 relative">
          <div className="absolute inset-0 bg-gray-900/5 dark:bg-gray-900/20 backdrop-blur-[0.5px] z-10 rounded-lg flex items-center justify-center">
            <div className="bg-white/95 dark:bg-gray-800/95 px-4 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">🔒 Ez a funkció jelenleg nem elérhető</p>
            </div>
          </div>
          <div className="flex pointer-events-none opacity-70">
            <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <UserCircleIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Vezető / Sofőr</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Vezető kiválasztása CRM felhasználóból */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vezető (CRM felhasználó)
                </label>
                <select
                  value={formData.assignedTo || ''}
                  onChange={(e) => {
                    handleChange('assignedTo', e.target.value || undefined);
                    // Ha van kiválasztott CRM felhasználó, töröljük a kézi nevet
                    if (e.target.value) {
                      handleChange('assignedToName', undefined);
                    }
                  }}
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
                >
                  <option value="">-- Nincs CRM felhasználó --</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.displayName || user.email}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Válassz CRM felhasználót, ha a vezető használja a rendszert
                </p>
              </div>

              {/* Vezető neve (ha nem CRM felhasználó) */}
              {!formData.assignedTo && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Vezető neve (ha nem CRM felhasználó)
                  </label>
                  <input
                    type="text"
                    value={formData.assignedToName || ''}
                    onChange={(e) => handleChange('assignedToName', e.target.value)}
                    placeholder="Pl. Kiss János (külső sofőr)"
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Ha a vezető nem használja a CRM-et, add meg a nevét
                  </p>
                </div>
              )}

              {/* Jogosítvány száma */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Jogosítvány száma
                </label>
                <input
                  type="text"
                  value={formData.driverLicenseNumber || ''}
                  onChange={(e) => handleChange('driverLicenseNumber', e.target.value)}
                  placeholder="AB123456"
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Vezető megjegyzések */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Megjegyzések a vezetőről
                </label>
                <textarea
                  rows={2}
                  value={formData.driverNotes || ''}
                  onChange={(e) => handleChange('driverNotes', e.target.value)}
                  placeholder="Pl. Csak nappali vezetés, stb..."
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bérlési beállítások Section (Flexie-car integráció) */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-indigo-100 to-blue-100 dark:from-indigo-900/20 dark:to-blue-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <GlobeAltIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Bérlési beállítások (Flexie-car)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Elérhető bérlésre - Csak láthatóság kapcsoló */}
              <div className="md:col-span-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <label className="flex items-start cursor-pointer">
                  <div className="relative flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={formData.rentalAvailable || false}
                      onChange={(e) => handleChange('rentalAvailable', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                  </div>
                  <div className="ml-3 flex-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 block">Elérhető bérlésre a weboldalon</span>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {formData.rentalAvailable 
                        ? '✓ Az autó látható a flexie-car weboldalon és foglalható' 
                        : '✗ Az autó rejtett a weboldalon, de a bérlési adatok megmaradnak'}
                    </p>
                  </div>
                </label>
              </div>

              {/* Kategória */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Autó kategória
                </label>
                <select
                  value={formData.category || ''}
                  onChange={(e) => handleChange('category', e.target.value || undefined)}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
                >
                  <option value="">Nincs kiválasztva</option>
                  <option value="mini">Mini</option>
                  <option value="economy">Gazdaságos (Economy)</option>
                  <option value="economy-suv">Gazdaságos SUV</option>
                  <option value="compact">Kompakt</option>
                  <option value="compact-suv">Kompakt SUV</option>
                  <option value="compact-wagon">Kompakt Kombi</option>
                  <option value="intermediate">Közepes (Intermediate)</option>
                  <option value="standard">Standard</option>
                  <option value="standard-suv">Standard SUV</option>
                  <option value="premium">Prémium</option>
                  <option value="luxury">Luxus</option>
                  <option value="ultimate-luxury">Csúcs Luxus</option>
                  <option value="small-panel-van">Kis Furgon</option>
                  <option value="panel-van-lwb">Nagy Furgon (LWB)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  A kategória segít a weboldalon a szűrésben és csoportosításban
                </p>
              </div>

              {/* Darabszám */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Darabszám (készlet)
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.quantity || ''}
                  onChange={(e) => handleChange('quantity', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="1"
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Ha több ugyanilyen autó van készleten, add meg a darabszámot. Ez nem látszik a weboldalon, de segít a foglalások kezelésében.
                </p>
              </div>

              {/* Bérlési adatok - MINDIG láthatóak */}
              <>
                  {/* Napi bérleti díj */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Napi bérleti díj *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        required={formData.rentalAvailable}
                        value={formData.pricePerDay || ''}
                        onChange={(e) => handleChange('pricePerDay', e.target.value ? parseFloat(e.target.value) : undefined)}
                        placeholder="pl. 15000"
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                      />
                      <select
                        value={formData.currency || 'HUF'}
                        onChange={(e) => handleChange('currency', e.target.value as 'HUF' | 'EUR' | 'USD')}
                        className="w-24 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
                      >
                        <option value="HUF">HUF</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                  </div>

                  {/* Váltó típusa */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Váltó típusa
                    </label>
                    <select
                      value={formData.transmission || ''}
                      onChange={(e) => handleChange('transmission', e.target.value || undefined)}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
                    >
                      <option value="">-- Válassz --</option>
                      <option value="manual">Manuális</option>
                      <option value="automatic">Automata</option>
                    </select>
                  </div>

                  {/* Minimum bérlési napok */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Minimum bérlési napok
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.minRentalDays || 1}
                      onChange={(e) => handleChange('minRentalDays', e.target.value ? parseInt(e.target.value) : 1)}
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                    />
                  </div>

                  {/* Maximum bérlési napok */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Maximum bérlési napok
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.maxRentalDays || ''}
                      onChange={(e) => handleChange('maxRentalDays', e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="Nincs limit"
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                    />
                  </div>

                  {/* Kaució összege */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Kaució ({formData.currency || 'HUF'})
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.depositAmount || ''}
                      onChange={(e) => handleChange('depositAmount', e.target.value ? parseFloat(e.target.value) : undefined)}
                      placeholder={formData.currency === 'EUR' ? 'pl. 500' : formData.currency === 'USD' ? 'pl. 600' : 'pl. 100000'}
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                    />
                  </div>

                  {/* Képek feltöltése (flexie-car weboldalhoz) */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Autó képek (weboldalon megjelenő képek)
                    </label>
                    <ImageUploadDropzone
                      photos={formData.photos || []}
                      onPhotosChange={(photos) => {
                        handleChange('photos', photos);
                        // imageUrl beállítása a primary kép alapján
                        const primaryPhoto = photos.find(p => p.isPrimary);
                        handleChange('imageUrl', primaryPhoto?.url);
                      }}
                      vehicleId={vehicle?.id}
                    />
                  </div>

                  {/* Leírás magyarul */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Autó leírása (Magyar) 🇭🇺
                    </label>
                    <textarea
                      rows={4}
                      value={formData.descriptionHu || ''}
                      onChange={(e) => handleChange('descriptionHu', e.target.value || undefined)}
                      placeholder="Részletes leírás az autóról magyarul... (pl. A Porsche Cayenne E-Hybrid egy nagy teljesítményű plug-in hibrid SUV...)"
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Ez a szöveg jelenik meg a magyar nyelvű weboldalon az autó részletes oldalán
                    </p>
                  </div>

                  {/* Leírás angolul */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Autó leírása (Angol) 🇬🇧
                    </label>
                    <textarea
                      rows={4}
                      value={formData.descriptionEn || ''}
                      onChange={(e) => handleChange('descriptionEn', e.target.value || undefined)}
                      placeholder="Detailed description in English... (e.g. The Porsche Cayenne E-Hybrid is a high-performance plug-in hybrid SUV...)"
                      className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      This text will appear on the English website on the vehicle details page
                    </p>
                  </div>

                  {/* Jellemzők / Features */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Jellemzők (Features - weboldalon angolul jelennek meg)
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {/* Air Conditioning */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Air Conditioning') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Air Conditioning']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Air Conditioning'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Légkondicionáló (Air Conditioning)</span>
                      </label>

                      {/* GPS Navigation */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('GPS Navigation') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'GPS Navigation']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'GPS Navigation'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">GPS Navigáció (GPS Navigation)</span>
                      </label>

                      {/* Bluetooth */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Bluetooth') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Bluetooth']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Bluetooth'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Bluetooth</span>
                      </label>

                      {/* USB Port */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('USB Port') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'USB Port']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'USB Port'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">USB Port</span>
                      </label>

                      {/* Backup Camera */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Backup Camera') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Backup Camera']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Backup Camera'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Tolatókamera (Backup Camera)</span>
                      </label>

                      {/* Parking Sensors */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Parking Sensors') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Parking Sensors']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Parking Sensors'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Parkolóradar (Parking Sensors)</span>
                      </label>

                      {/* Cruise Control */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Cruise Control') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Cruise Control']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Cruise Control'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Tempomat (Cruise Control)</span>
                      </label>

                      {/* Heated Seats */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Heated Seats') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Heated Seats']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Heated Seats'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Fűthető ülések (Heated Seats)</span>
                      </label>

                      {/* Leather Seats */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Leather Seats') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Leather Seats']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Leather Seats'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Bőr ülések (Leather Seats)</span>
                      </label>

                      {/* Sunroof */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Sunroof') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Sunroof']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Sunroof'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Napfénytető (Sunroof)</span>
                      </label>

                      {/* Child Seat Available */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Child Seat Available') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Child Seat Available']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Child Seat Available'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Gyerekülés elérhető (Child Seat Available)</span>
                      </label>

                      {/* Winter Tires */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Winter Tires') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Winter Tires']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Winter Tires'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Téli gumi (Winter Tires)</span>
                      </label>

                      {/* Apple CarPlay */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Apple CarPlay') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Apple CarPlay']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Apple CarPlay'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Apple CarPlay</span>
                      </label>

                      {/* Android Auto */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Android Auto') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Android Auto']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Android Auto'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Android Auto</span>
                      </label>

                      {/* Lane Assist */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Lane Assist') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Lane Assist']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Lane Assist'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Sávtartó asszisztens (Lane Assist)</span>
                      </label>

                      {/* Blind Spot Monitor */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Blind Spot Monitor') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Blind Spot Monitor']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Blind Spot Monitor'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Holttér figyelő (Blind Spot Monitor)</span>
                      </label>

                      {/* Keyless Entry */}
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features?.includes('Keyless Entry') || false}
                          onChange={(e) => {
                            const features = formData.features || [];
                            if (e.target.checked) {
                              handleChange('features', [...features, 'Keyless Entry']);
                            } else {
                              handleChange('features', features.filter(f => f !== 'Keyless Entry'));
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Kulcs nélküli nyitás (Keyless Entry)</span>
                      </label>
                    </div>

                    {/* Luggage Capacity */}
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Csomagtér kapacitás (Luggage Capacity)
                      </label>
                      <select
                        value={formData.features?.find(f => f.includes('Luggage')) || ''}
                        onChange={(e) => {
                          const features = formData.features?.filter(f => !f.includes('Luggage')) || [];
                          if (e.target.value) {
                            handleChange('features', [...features, e.target.value]);
                          } else {
                            handleChange('features', features);
                          }
                        }}
                        className="w-full md:w-1/2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 text-gray-700 dark:text-gray-200"
                      >
                        <option value="">-- Select Luggage Capacity --</option>
                        <option value="1 Large Luggage">1 Large Luggage</option>
                        <option value="2 Large Luggage">2 Large Luggage</option>
                        <option value="3 Large Luggage">3 Large Luggage</option>
                        <option value="4 Large Luggage">4 Large Luggage</option>
                        <option value="5+ Large Luggage">5+ Large Luggage</option>
                        <option value="1 Large + 2 Small Luggage">1 Large + 2 Small Luggage</option>
                        <option value="2 Large + 2 Small Luggage">2 Large + 2 Small Luggage</option>
                        <option value="3 Large + 2 Small Luggage">3 Large + 2 Small Luggage</option>
                      </select>
                    </div>

                    <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                      Ezek a jellemzők angolul jelennek meg a weboldalon (flexie-car)
                    </p>
                  </div>
                </>
              </div>
            </div>
          </div>
        </div>

        {/* Okmányok & Lejáratok Section - TILTOTT */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 relative">
          <div className="absolute inset-0 bg-gray-900/5 dark:bg-gray-900/20 backdrop-blur-[0.5px] z-10 rounded-lg flex items-center justify-center">
            <div className="bg-white/95 dark:bg-gray-800/95 px-4 py-2 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">🔒 Ez a funkció jelenleg nem elérhető</p>
            </div>
          </div>
          <div className="flex pointer-events-none opacity-70">
            <div className="p-4 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <DocumentCheckIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Okmányok & Lejáratok</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Forgalmi engedély száma */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Forgalmi engedély száma
                </label>
                <input
                  type="text"
                  value={formData.registrationNumber || ''}
                  onChange={(e) => handleChange('registrationNumber', e.target.value)}
                  placeholder="AB-123456"
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Törzskönyv száma */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Törzskönyv száma
                </label>
                <input
                  type="text"
                  value={formData.vehicleLogbookNumber || ''}
                  onChange={(e) => handleChange('vehicleLogbookNumber', e.target.value)}
                  placeholder="123456"
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Műszaki vizsga lejárat */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Műszaki vizsga lejárata
                </label>
                <input
                  type="date"
                  value={formData.inspectionExpiryDate ? new Date(formData.inspectionExpiryDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => handleChange('inspectionExpiryDate', e.target.value ? new Date(e.target.value) : undefined)}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Biztosítás lejárat */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Biztosítás lejárata
                </label>
                <input
                  type="date"
                  value={formData.insuranceExpiryDate ? new Date(formData.insuranceExpiryDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => handleChange('insuranceExpiryDate', e.target.value ? new Date(e.target.value) : undefined)}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Következő szerviz dátuma */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Következő szerviz dátuma
                </label>
                <input
                  type="date"
                  value={formData.nextServiceDate ? new Date(formData.nextServiceDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => handleChange('nextServiceDate', e.target.value ? new Date(e.target.value) : undefined)}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>

              {/* Következő szerviz km óra */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Következő szerviz km óránál
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.nextServiceMileage || ''}
                  onChange={(e) => handleChange('nextServiceMileage', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="pl. 50000"
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500 placeholder-gray-500 text-gray-700 dark:text-gray-200 dark:bg-gray-700"
                />
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            {t('fleet.form.buttons.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? t('fleet.form.buttons.save') : vehicle ? t('fleet.form.buttons.update') : t('fleet.form.buttons.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default VehicleEditor;
